import { useState } from "react";
import { api, API, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Download, Printer, RefreshCw, Ban, QrCode, Loader2, Copy, TriangleAlert } from "lucide-react";
import { toast } from "sonner";

const BACKEND_ORIGIN = API.replace(/\/api$/, "");
const abs = (u) => (u ? `${BACKEND_ORIGIN}${u}` : "");

export default function PreviewQRCard({ qr, onRegenerate, onRevoke, busy = false, showRevoke = false, compact = false }) {
  const [downloading, setDownloading] = useState(false);

  const downloadFile = async (url, ext) => {
    if (!url) return;
    setDownloading(true);
    try {
      const path = url.replace(/^\/api/, "");
      const { data } = await api.get(path, { responseType: "blob" });
      const href = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = href;
      a.download = `velopaths-qr-${(qr.shopName || "shop").replace(/\s+/g, "-").toLowerCase()}.${ext}`;
      a.click();
      URL.revokeObjectURL(href);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setDownloading(false);
    }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(qr.previewUrl); toast.success("Link copied"); }
    catch { toast.message("Preview link", { description: qr.previewUrl }); }
  };

  const print = () => {
    const w = window.open("", "_blank", "width=520,height=720");
    if (!w) return toast.error("Please allow pop-ups to print the QR code.");
    const png = abs(qr.qrImageUrl);
    const date = new Date().toLocaleDateString();
    w.document.write(`<!doctype html><html><head><title>Rider Preview QR</title>
      <style>
        body{font-family:system-ui,Arial,sans-serif;text-align:center;padding:40px;color:#0f172a}
        h1{font-size:22px;margin:0 0 4px}
        h2{font-size:15px;font-weight:400;color:#475569;margin:0 0 24px}
        img{width:340px;height:340px}
        .label{font-size:16px;font-weight:700;margin-top:16px}
        .date{font-size:12px;color:#94a3b8;margin-top:8px}
      </style></head><body>
      <h1>${qr.shopName || "Shop"}</h1>
      <h2>${qr.bikePathName || ""}</h2>
      <img src="${png}" onload="setTimeout(()=>{window.print();},250)" />
      <div class="label">Scan to Preview Rider Map</div>
      <div class="date">Generated ${date}</div>
      </body></html>`);
    w.document.close();
  };

  if (!qr || !qr.id) {
    return (
      <div className="rounded-2xl border border-dashed border-border bg-card p-6 text-center" data-testid="qr-not-generated">
        <QrCode className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
        <div className="text-sm text-muted-foreground mb-3">No QR code yet for {qr?.shopName || "this shop"}.</div>
        <Button size="sm" onClick={onRegenerate} disabled={busy} data-testid="qr-generate-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <QrCode className="w-4 h-4 mr-1" />}Generate QR Code
        </Button>
      </div>
    );
  }

  if (qr.revoked) {
    return (
      <div className="rounded-2xl border border-red-500/40 bg-red-500/5 p-6 text-center" data-testid="qr-revoked">
        <TriangleAlert className="w-8 h-8 text-red-500 mx-auto mb-2" />
        <div className="font-heading font-bold text-red-500">Preview link revoked</div>
        <p className="text-sm text-muted-foreground mt-1 mb-3">This QR code is no longer valid. Generate a new link and QR code to share again.</p>
        <Button size="sm" onClick={onRegenerate} disabled={busy} data-testid="qr-generate-new-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}Generate new link + QR
        </Button>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid="qr-card">
      <div className={`flex ${compact ? "flex-col sm:flex-row" : "flex-col md:flex-row"} gap-5 items-center`}>
        <div className="bg-white rounded-xl p-3 shrink-0" data-testid="qr-image-box">
          <img src={abs(qr.qrImageUrl)} alt="Rider preview QR code" className="w-44 h-44" data-testid="qr-image" />
        </div>
        <div className="flex-1 min-w-0 w-full">
          {qr.shopName && <div className="font-heading font-bold">{qr.shopName}</div>}
          {qr.bikePathName && <div className="text-xs text-muted-foreground mb-1">{qr.bikePathName}</div>}
          <div className="text-xs font-semibold text-primary mb-2">Scan to Preview Rider Map</div>
          <div className="text-[11px] text-muted-foreground break-all bg-secondary rounded p-2 mb-3" data-testid="qr-url">{qr.previewUrl}</div>
          <div className="flex flex-wrap gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" disabled={downloading} data-testid="qr-download-trigger">
                  {downloading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}Download
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => downloadFile(qr.qrImageUrl, "png")} data-testid="qr-download-png">PNG (image)</DropdownMenuItem>
                <DropdownMenuItem onClick={() => downloadFile(qr.qrSvgUrl, "svg")} data-testid="qr-download-svg">SVG (vector)</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button size="sm" variant="outline" onClick={print} data-testid="qr-print"><Printer className="w-4 h-4 mr-1" />Print</Button>
            <Button size="sm" variant="outline" onClick={copyLink} data-testid="qr-copy"><Copy className="w-4 h-4 mr-1" />Copy link</Button>
            <Button size="sm" variant="outline" onClick={onRegenerate} disabled={busy} data-testid="qr-regenerate">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}Regenerate
            </Button>
            {showRevoke && onRevoke && (
              <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={onRevoke} disabled={busy} data-testid="qr-revoke"><Ban className="w-4 h-4 mr-1" />Revoke</Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
